import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  ChevronLeft, ChevronRight, Activity, ShieldAlert, Globe, 
  Monitor, Trash2, AlertTriangle, Clock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ActivityLogs = ({ user, onLogout }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 50;

  // Failed Access Logs State
  const [failedLogs, setFailedLogs] = useState([]);
  const [failedLoading, setFailedLoading] = useState(true);
  const [failedPage, setFailedPage] = useState(1);
  const [failedTotal, setFailedTotal] = useState(0);
  const [failedStats, setFailedStats] = useState(null);
  const [activeTab, setActiveTab] = useState('activity');

  useEffect(() => {
    fetchLogs();
  }, [page]);

  useEffect(() => {
    if (activeTab === 'blocked') {
      fetchFailedLogs();
    }
  }, [failedPage, activeTab]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/activity/logs`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, page_size: pageSize },
      });
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch activity logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchFailedLogs = async () => {
    setFailedLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/failed-access-logs`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: failedPage, page_size: pageSize },
      });
      setFailedLogs(response.data.logs);
      setFailedTotal(response.data.total);
      setFailedStats(response.data.stats);
    } catch (error) {
      toast.error('Failed to fetch blocked access logs');
    } finally {
      setFailedLoading(false);
    }
  };

  const clearFailedLogs = async () => {
    if (!window.confirm('Are you sure you want to clear all blocked access logs?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/admin/failed-access-logs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Blocked access logs cleared');
      fetchFailedLogs();
    } catch (error) {
      toast.error('Failed to clear logs');
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const parseUserAgent = (ua) => {
    if (!ua || ua === 'Unknown') return { browser: 'Unknown', os: 'Unknown' };
    
    let browser = 'Unknown';
    let os = 'Unknown';
    
    // Detect browser
    if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Safari')) browser = 'Safari';
    else if (ua.includes('Edge')) browser = 'Edge';
    else if (ua.includes('Opera')) browser = 'Opera';
    
    // Detect OS
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
    
    return { browser, os };
  };

  const totalPages = Math.ceil(total / pageSize);
  const failedTotalPages = Math.ceil(failedTotal / pageSize);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="flex-1 p-8 overflow-auto bg-gradient-to-br from-slate-50 to-slate-100" data-testid="activity-logs">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="bg-blue-600 p-3 rounded-xl">
              <Activity className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">System Logs</h1>
              <p className="text-slate-500 text-sm">Monitor system activities and access attempts</p>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-white border border-slate-200 p-1">
              <TabsTrigger 
                value="activity" 
                className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
              >
                <Activity className="w-4 h-4 mr-2" />
                Activity Logs
              </TabsTrigger>
              <TabsTrigger 
                value="blocked"
                className="data-[state=active]:bg-red-600 data-[state=active]:text-white"
              >
                <ShieldAlert className="w-4 h-4 mr-2" />
                Blocked Access
                {failedStats?.total_attempts > 0 && (
                  <Badge variant="destructive" className="ml-2 bg-red-500">
                    {failedStats.total_attempts}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Activity Logs Tab */}
            <TabsContent value="activity" className="mt-4">
              <Card className="border-slate-200 shadow-sm bg-white">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader className="bg-slate-50 border-b border-slate-200">
                        <TableRow>
                          <TableHead className="font-semibold text-slate-600 w-[200px]">Timestamp</TableHead>
                          <TableHead className="font-semibold text-slate-600">User</TableHead>
                          <TableHead className="font-semibold text-slate-600">Action</TableHead>
                          <TableHead className="font-semibold text-slate-600">Details</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loading ? (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                              Loading logs...
                            </TableCell>
                          </TableRow>
                        ) : logs.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                              No activity logs found
                            </TableCell>
                          </TableRow>
                        ) : (
                          logs.map((log, index) => (
                            <TableRow key={index} className="hover:bg-slate-50 transition-colors border-b border-slate-100">
                              <TableCell className="font-mono text-xs text-slate-600">{formatTimestamp(log.timestamp)}</TableCell>
                              <TableCell className="font-medium text-slate-800">{log.user_email}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                  {log.action}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-sm text-slate-600">{log.details}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-slate-500">
                    Page {page} of {totalPages} • Total: {total} logs
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="border-slate-300"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page === totalPages}
                      className="border-slate-300"
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Blocked Access Tab */}
            <TabsContent value="blocked" className="mt-4 space-y-4">
              {/* Stats Cards */}
              {failedStats && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="border-slate-200 shadow-sm bg-white">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-red-100 p-2.5 rounded-lg">
                          <AlertTriangle className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Total Blocked</p>
                          <p className="text-2xl font-bold text-slate-800">{failedStats.total_attempts}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200 shadow-sm bg-white">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-amber-100 p-2.5 rounded-lg">
                          <Clock className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Today's Attempts</p>
                          <p className="text-2xl font-bold text-slate-800">{failedStats.today_attempts}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200 shadow-sm bg-white">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-purple-100 p-2.5 rounded-lg">
                          <Globe className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Unique IPs</p>
                          <p className="text-2xl font-bold text-slate-800">{failedStats.unique_ips}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFailedLogs}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  disabled={failedTotal === 0}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear All Logs
                </Button>
              </div>

              {/* Failed Access Table */}
              <Card className="border-slate-200 shadow-sm bg-white">
                <CardHeader className="border-b border-slate-100 pb-3">
                  <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <ShieldAlert className="w-5 h-5 text-red-600" />
                    Blocked Access Attempts (IP Restriction)
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader className="bg-slate-50 border-b border-slate-200">
                        <TableRow>
                          <TableHead className="font-semibold text-slate-600 w-[180px]">Timestamp</TableHead>
                          <TableHead className="font-semibold text-slate-600">IP Address</TableHead>
                          <TableHead className="font-semibold text-slate-600">Device / Browser</TableHead>
                          <TableHead className="font-semibold text-slate-600">Operating System</TableHead>
                          <TableHead className="font-semibold text-slate-600">Reason</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {failedLoading ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                              Loading blocked access logs...
                            </TableCell>
                          </TableRow>
                        ) : failedLogs.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                              <div className="flex flex-col items-center gap-2">
                                <ShieldAlert className="w-10 h-10 text-slate-300" />
                                <p>No blocked access attempts recorded</p>
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : (
                          failedLogs.map((log, index) => {
                            const { browser, os } = parseUserAgent(log.user_agent);
                            return (
                              <TableRow key={index} className="hover:bg-red-50/50 transition-colors border-b border-slate-100">
                                <TableCell className="font-mono text-xs text-slate-600">
                                  {formatTimestamp(log.timestamp)}
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <Globe className="w-4 h-4 text-slate-400" />
                                    <span className="font-mono text-sm font-medium text-slate-800">{log.ip_address}</span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <Monitor className="w-4 h-4 text-slate-400" />
                                    <span className="text-sm text-slate-700">{browser}</span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">
                                    {os}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge variant="destructive" className="bg-red-100 text-red-700 border-red-200">
                                    {log.reason}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {failedTotalPages > 1 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-500">
                    Page {failedPage} of {failedTotalPages} • Total: {failedTotal} attempts
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setFailedPage(failedPage - 1)}
                      disabled={failedPage === 1}
                      className="border-slate-300"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setFailedPage(failedPage + 1)}
                      disabled={failedPage === failedTotalPages}
                      className="border-slate-300"
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
        <Footer />
      </main>
    </div>
  );
};

export default ActivityLogs;
